import type { CSSProperties } from "react";
import { useTranslation } from "react-i18next";

import type { AppEntry, Locale } from "../types";
import { Glyph } from "./Glyphs";

interface Props {
  app: AppEntry;
}

function hexToRgbTriplet(hex: string): string | null {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim());
  if (!m) return null;
  return `${parseInt(m[1], 16)} ${parseInt(m[2], 16)} ${parseInt(m[3], 16)}`;
}

export function AppCard({ app }: Props) {
  const { t, i18n } = useTranslation("launcher");
  const lng: Locale = i18n.resolvedLanguage === "en" ? "en" : "es";

  const brandTriplet = hexToRgbTriplet(app.color);
  const style = brandTriplet
    ? ({ "--card-brand": brandTriplet } as CSSProperties)
    : undefined;

  const desc = app.description[lng] || app.description.es;
  const cat = app.category[lng] || app.category.es;

  return (
    <a
      href={app.url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t("card.openAria", { name: app.name })}
      style={style}
      className="group h-full flex flex-col bg-surface-subtle/20 border border-border-subtle rounded-sm overflow-hidden transition-colors duration-150 hover:bg-surface-subtle/50 hover:border-card-brand/40 focus-visible:outline-none focus-visible:border-card-brand/60 focus-visible:shadow-[0_0_0_2px_rgb(var(--card-brand)/0.4)] text-inherit no-underline"
    >
      <div
        aria-hidden="true"
        className="relative h-[88px] flex items-center justify-center bg-surface-inset/50 border-b border-border-subtle overflow-hidden"
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(135deg, rgb(var(--card-brand) / 0.18) 0%, rgb(var(--card-brand) / 0.04) 100%)",
          }}
        />
        {app.image ? (
          <img
            src={app.image}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="relative w-[70%] max-w-[200px] h-11 text-card-brand opacity-75 group-hover:opacity-100 transition-opacity duration-150">
            <Glyph kind={app.glyph} className="w-full h-full" />
          </div>
        )}
        <span
          className="absolute top-1.5 right-1.5 w-[22px] h-[22px] inline-flex items-center justify-center bg-surface-base/60 border border-border-subtle rounded-sm text-text-muted/70 transition-colors duration-150 group-hover:text-card-brand group-hover:border-card-brand/50"
          style={{ backdropFilter: "blur(4px)" }}
        >
          <span className="material-symbols-outlined text-[14px]">launch</span>
        </span>
      </div>

      <div className="flex flex-col gap-2 p-3 flex-1">
        <div className="flex flex-col gap-[2px] min-w-0">
          <p
            className="m-0 text-13 font-semibold text-text-main truncate"
            title={app.name}
          >
            {app.name}
          </p>
          <p className="m-0 text-10 font-bold uppercase tracking-widerx text-card-brand/85">
            {cat}
          </p>
        </div>

        <p className="m-0 text-12 leading-[17px] text-text-muted flex-1">{desc}</p>

        {app.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {app.tags.map(tag => (
              <span
                key={tag}
                className="font-mono text-10 leading-[14px] text-text-muted bg-surface-subtle/50 border border-outline-variant/25 rounded-sm px-1.5 py-px"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </a>
  );
}
